import {
  Transforms,
  Node as SlateNode,
  Text as SlateText,
  Range as SlateRange,
  Editor,
  BaseRange,
  Path as SlatePath,
  Element as SlateElement,
} from 'slate';
import { ReactEditor } from 'slate-react';
import {
  RichTextBoxEditor,
  RichTextBoxFormatValue,
} from './richTextEditorTypes';
import { RichTextBoxUtils as _rtb } from './RichTextBoxUtils';
import { isHotkey } from 'is-hotkey';
import { TextDirection } from 'slate/dist/interfaces/types';
import { RichTextBoxSerialization } from './RichTextBoxSerialization';

type HotKeyType = {
  [index: string]: string;
};

//hot keys used for toolbar buttons
const HOTKEYS: HotKeyType = {
  'ctrl+b': 'bold',
  'ctrl+i': 'italic',
  'ctrl+u': 'underline',
  'ctrl+\\': 'clear',
  'ctrl+shift+1': 'bullet-list',
  'ctrl+shift+2': 'number-list',
  'ctrl+space': 'tab',
  'ctrl+shift+space': 'tab',
};

/**
 * You can override how things behave in slate using this hook.  For example if you want to support inline images, you can add
 * an image element then down in the isVoid and isInLine functions you can return nothing for images (no text)
 * and say they can be inline
 * @param editor
 * @returns
 */
export function withRichTextBox(editor: RichTextBoxEditor, uid: string) {
  const {
    insertText,
    insertBreak,
    insertFragment,
    insertNode,
    addMark,
    removeMark,
    deleteFragment,
  } = editor;
  const macroKey = '/';
  const isDom = typeof document === 'object';
  const hasNoModiferKey = (event: React.KeyboardEvent<Element>) =>
    !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey;
  let macroMightBeOpen = false;

  /**
   * Returns all text nodes that have isMacroSearch turned on
   * Important to only use methods that search the Editor after all changes have occurred from previous operations
   * I.e. do inserts/transforms on keydown or keyup, and do all the checks in OnSelect
   * @returns
   */
  const getMacroSearchTextNodes = () => {
    if (!editor) return false;
    const [match] = Array.from(
      Editor.nodes(editor, {
        at: [],
        match: (n) => {
          return (
            !Editor.isEditor(n) &&
            SlateText.isText(n) &&
            Boolean(n.isMacroSearch)
          );
        },
        mode: 'all',
        universal: false,
      }),
    );
    return match && match.length > 0;
  };

  /**
   * Called when the macro hot key is pressed.  If the editor is selected and has focus
   * the current cursor locotion will have the mark for "isMacroSearch" turned on which will
   * enabled the editor leaf element to render a dropdown there.
   * @returns
   */
  const processMacroKeyPress = () => {
    if (
      !editor.selection ||
      !editor.isSelectionSingleCursor(
        Editor.unhangRange(editor, editor.selection),
      )
    )
      return false;

    const node = editor.getFocusedTextNodeAtCursor();
    if (!node) return false;

    if (node.textNode.isMacroSearch) {
      editor.closeMacros();
    }
    editor.addMark('isMacroSearch', true);
    return true;
  };

  //DevNote: moved to editor, Utils didn't need it anymore.
  /**
   * Returns all the top level currently selected block elements and has a match function to narrow them down by type, etc.
   * @param editor
   * @param match
   * @returns
   */
  const getSelectedBlockNodes = (
    editor: Editor,
    match?: (n: SlateNode | SlatePath) => boolean,
  ) => {
    const { selection } = editor;
    if (selection) {
      const startIndex = SlateRange.isBackward(selection)
        ? selection.focus.path[0]
        : selection.anchor.path[0];
      const endIndex = SlateRange.isBackward(selection)
        ? selection.anchor.path[0]
        : selection.focus.path[0];
      const nodes = [];
      for (let index = startIndex; index <= endIndex; ++index) {
        nodes.push(editor.children[index]);
      }
      return nodes.filter((n) => !match || match(n));
    }
    return [];
  };

  editor.uid = uid;

  /**
   * Closes any existing open macros and sets macroMightBeOpen to false
   */
  editor.closeMacros = (deleteSlash?: boolean) => {
    macroMightBeOpen = false;
    //see if any nodes have isMacroSearch turned on
    const macroNodes = getMacroSearchTextNodes();
    if (macroNodes) {
      if (deleteSlash)
        //instead of unsetting macroSearch on the current textnode, delete it entirely.
        Transforms.removeNodes(editor, {
          at: [],
          match: (n) => {
            return (
              !Editor.isEditor(n) &&
              SlateText.isText(n) &&
              Boolean(n.isMacroSearch)
            );
          },
          hanging: true,
        });
      else
        Transforms.unsetNodes(editor, 'isMacroSearch', {
          at: [],
          match: (n) => {
            return (
              !Editor.isEditor(n) &&
              SlateText.isText(n) &&
              Boolean(n.isMacroSearch)
            );
          },
          hanging: true,
          mode: 'all',
        });
    }
  };

  editor.onKeyDown = (event) => {
    if (!editor.selection || !ReactEditor.isFocused(editor)) return;

    if (isDom && hasNoModiferKey(event)) {
      if (event.key === macroKey) {
        if (processMacroKeyPress()) {
          //macro mark was set, set that the macro might be open
          macroMightBeOpen = true;
        }
        return;
      }
    }

    if (event.key === 'Escape') {
      if (Editor.marks(editor)?.isMacroSearch) {
        editor.closeMacros();
      }
    }

    if (event.key === ' ') {
      if (Editor.marks(editor)?.isMacroSearch) {
        //<- this is only if the existing selection has a macro dropdown open, space will close it
        editor.closeMacros();
      }
    }

    if (event.key === 'Enter') {
      if (editor.selection) {
        //break out of lists if enter on empty item
        if (_rtb.tryRemoveEmptyListItemAndBreak(editor)) {
          event.preventDefault();
        }
      }
      return;
    }

    //all hot keys are ctrl/command modifiers
    if (!event.ctrlKey) {
      return;
    }

    for (const hotkey in HOTKEYS) {
      if (isHotkey(hotkey, event as any)) {
        event.preventDefault();
        const mark = HOTKEYS[hotkey];
        switch (mark) {
          case 'tab':
            {
              editor.changeBlockIndent(!event.shiftKey ? undefined : true);
            }
            break;
          case 'bold':
            _rtb.toggleFormat(editor, RichTextBoxFormatValue.bold);
            break;
          case 'italic':
            _rtb.toggleFormat(editor, RichTextBoxFormatValue.italic);
            break;
          case 'underline':
            _rtb.toggleFormat(editor, RichTextBoxFormatValue.underline);
            break;
          case 'clear':
            editor.removeMark('format');
            editor.removeMark('color');
            _rtb.toggleBlock(editor, 'paragraph', true);
            break;
          default:
            break;
        }
      }
    }
  };

  /**
   * Deletes the macro search and / placeholder and inserts the specified macro content into the dom at the next block location.
   * @param macro
   * @returns
   */
  editor.insertMacro = (html: string) => {
    if (!editor || !editor.selection) return false;

    const doc = RichTextBoxSerialization.parseHtmlToRichText(html);
    if (doc) {
      editor.closeMacros(true);
      Transforms.insertNodes(editor, doc, { at: editor.selection });
      return true;
    }
    return false;
  };

  //Select happens when the selection changes in the content editable,
  //this fires after all changes have happened
  editor.onSelect = () => {
    if (!editor.selection || !ReactEditor.isFocused(editor)) return; //not selected

    const currentNode = editor.getFocusedTextNodeAtCursor();
    if (currentNode) {
      if (!currentNode.textNode.isMacroSearch) {
        if (macroMightBeOpen) {
          //anytime the selection changes to a selection that isn't a macro dropdown, close any existing macros if one might be open
          //this will close the macro if the user clicks on something outside of the macro element in the editor.
          editor.closeMacros();
        }
      } else if (
        currentNode.textNode.text.length === 0 ||
        !currentNode.textNode.text.startsWith(macroKey)
      ) {
        //if in a macro block but it's empty (the user deleted the start slash or the text in it, then close the macro dropdown)
        if (macroMightBeOpen) {
          editor.closeMacros();
        }
      }
    }
  };

  editor.insertSoftBreak = () => {
    if (macroMightBeOpen) {
      //don't let users do soft breaks in macro searches
      editor.closeMacros();
    }
    insertText('\n');
  };

  editor.insertText = (text) => {
    insertText(text);
  };

  editor.insertBreak = () => {
    if (editor && editor.marks && editor.marks['isMacroSearch']) {
      editor.closeMacros();
    }
    insertBreak();
  };

  editor.insertFragment = (fragment: SlateNode[]) => {
    insertFragment(fragment);
  };

  editor.insertNode = (node: SlateNode) => {
    insertNode(node);
  };

  editor.addMark = (mark: string, value: any) => {
    addMark(mark, value);
  };

  editor.removeMark = (key: string) => {
    removeMark(key);
  };

  editor.deleteFragment = (direction?: TextDirection | undefined) => {
    deleteFragment(direction);
  };

  //#region Extended Helpers
  /**
   * Determines if the selection is a single point indicating that it is where the cursor is.
   * If the selection is empty and contains the same paths in both anchor and in focus and the same offsets
   * in focus and in anchor then the selection is just where the cursor is     *
   * @param range
   * @returns True if the selection represents the cursor location, false if it's a bigger selection (like a block highlight)
   */
  editor.isSelectionSingleCursor = (range: BaseRange) => {
    if (
      _.isEqual(range.anchor.path, range.focus.path) &&
      _.isEqual(range.anchor.offset, range.focus.offset)
    )
      return true;
    return false;
  };

  /**
   * Get the currently selected text node at the curor and the cursors index in the text.
   * @returns
   */
  editor.getFocusedTextNodeAtCursor = () => {
    if (!ReactEditor.isFocused(editor) || !editor.selection) return null; //editor is not focused or has no selection (the cursor isn't anywhere either)

    const selection = Editor.unhangRange(editor, editor.selection);
    if (!editor.isSelectionSingleCursor(selection)) return null; //selection is not just a cursor

    if (SlateRange.isRange(selection)) {
      const node = SlateNode.get(editor, selection.focus.path);
      if (SlateText.isText(node))
        return {
          cursorOffset: selection.focus.offset,
          textNode: node,
        };
    }
    return null;
  };

  //DevNote: Moved changeBlockIndent into our withRichTextBox plugin to move things towards being editor available
  //you can get the editor via the useSlate hook, so it's better to move what makes since to our custom editor.
  /**
   * Change indentation of the current selection
   * @param editor
   * @param decrease true to reduce 1 level of identation, false or undefined to increase 1 level
   * @param indentationLevel if this is set the level goes directly to this number and decrease is ignored
   */
  editor.changeBlockIndent = (
    decrease?: boolean,
    indentationLevel?: number,
  ) => {
    const { selection } = editor;
    if (!selection) return;
    getSelectedBlockNodes(editor, (n) => SlateElement.isElement(n)).forEach(
      (node: SlateElement) => {
        let newLevel = indentationLevel;
        if (newLevel == null) {
          if (decrease)
            newLevel = Math.max((node.indentationLevel ?? 0) - 1, 0);
          else newLevel = (node.indentationLevel ?? 0) + 1;
        }
        if (newLevel != null)
          Transforms.setNodes(
            editor,
            { indentationLevel: newLevel },
            { at: ReactEditor.findPath(editor, node) },
          );
      },
    );
  };

  return editor;
}
